import React, { createContext, useState, useEffect, useContext } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load auth state from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedCompanyId = localStorage.getItem('company_id');

    // Only set valid tokens (not dummy/test tokens)
    if (
      storedToken &&
      !storedToken.startsWith('dummy-') &&
      !storedToken.startsWith('google-dummy-') &&
      !storedToken.startsWith('google-register-')
    ) {
      setToken(storedToken);
      setCompanyId(storedCompanyId);
      
      if (storedUser) {
        try {
          setUser(JSON.parse(storedUser));
        } catch (error) {
          console.error('Failed to parse stored user:', error);
          // Clear invalid user data
          localStorage.removeItem('user');
        }
      }
    } else {
      // Clear invalid tokens
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('company_id');
    }

    setLoading(false);
  }, []);

  const login = (authToken, userData) => {
    // Store in state
    setToken(authToken);
    setUser(userData);
    setCompanyId(userData.company_id);

    // Persist to localStorage
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('company_id', userData.company_id);

    console.log('User logged in:', userData.name);
    console.log('Token set:', authToken.substring(0, 20) + '...');
    console.log('💼 Company ID:', userData.company_id);
  };

  const logout = () => {
    // Clear state
    setToken(null);
    setUser(null);
    setCompanyId(null);

    // Clear authentication from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('company_id');
    localStorage.removeItem('user_feature');
    
    // Clear ALL appState page states
    localStorage.removeItem('appState_addProduct');
    localStorage.removeItem('appState_viewProducts');
    localStorage.removeItem('appState_ebaySettings');
    localStorage.removeItem('appState_generalSettings');
    localStorage.removeItem('appState_inviteUsers');
    
    // Clear SorterPage state
    localStorage.removeItem('sorter_selectedImages');
    localStorage.removeItem('sorter_results');
    
    // Clear any other potential cached data
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith('appState_') || key.startsWith('sorter_')) {
        localStorage.removeItem(key);
      }
    });

    console.log('🚪 User logged out - All states cleared');
  };

  const isAuthenticated = () => {
    return !!token && !token.startsWith('dummy-') && !token.startsWith('google-');
  };

  const value = {
    token,
    user,
    companyId,
    loading,
    login,
    logout,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
